import { Fragment, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Input,
  Dropdown,
  Option,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Spinner,
  Tooltip,
  Card,
  CardHeader,
} from '@fluentui/react-components';
import {
  SearchRegular,
  ArrowLeftFilled,
  ChevronLeftFilled,
  ChevronRightFilled,
  ArrowSwapFilled,
  ChevronDownRegular,
  ChevronRightRegular,
} from '@fluentui/react-icons';
import { useAuth } from '../auth/TeamsAuthProvider';
import { NativeDateInput } from '../common/NativeDateInput';
import { TimezoneSelector } from '../common/TimezoneSelector';
import { useTimezone } from '../common/TimezoneProvider';
import { StatusPill } from '../common/StatusPill';
import { PageHeader } from '../common/PageHeader';
import { ViewAsAction } from '../common/ViewAsAction';
import { LoadingState } from '../common/LoadingState';
import { ErrorState } from '../common/ErrorState';
import { EmptyState } from '../common/EmptyState';
import { CallLegsDetail } from './CallLegsDetail';
import { QualityCell } from './QualityCell';
import { ReportMissingCallDialog } from './ReportMissingCallDialog';
import { api } from '../../services/api';
import type { CallListResponse, CallFilters, CallLegsResponse } from '../../types';
import { formatPhoneNumber } from '../../utils/phone';
import { ReportingTimeDisplay } from '../common/SourceValueDisplay';
import { deriveOutcome, outcomeTone } from '../../utils/interactionOutcome';
import { interactionPathFor } from '../../utils/routes';
import { strings } from '../../strings/en';
import {
  buildDefaultCallHistorySearchParams,
  buildPresetRange,
  getStoredDatePreset,
  type DatePreset,
} from '../../utils/callHistoryFilters';

/** Last-10-digits comparison, matching the normalize used for journey "you" detection. */
function normalizePhoneForCompare(n: string | null | undefined): string {
  return (n ?? '').replace(/\D/g, '').slice(-10);
}

const useStyles = makeStyles({
  content: {
    padding: tokens.spacingHorizontalL,
    maxWidth: '1400px',
    margin: '0 auto',
  },
  backRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalM,
  },
  filters: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalL,
    flexWrap: 'wrap',
  },
  filterItem: {
    minWidth: '150px',
  },
  searchInput: {
    minWidth: '250px',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  timeCell: {
    minWidth: '132px',
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  paginationButtons: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  statusBanner: {
    marginBottom: tokens.spacingVerticalL,
  },
  statusBannerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  statusBannerText: {
    color: tokens.colorNeutralForeground3,
  },
  statusBannerError: {
    marginBottom: tokens.spacingVerticalL,
    borderLeft: `4px solid ${tokens.colorPaletteRedForeground1}`,
  },
  directionBadge: {
    textTransform: 'capitalize',
  },
  legsIconBadge: {
    marginLeft: '4px',
    cursor: 'help',
  },
  phoneNumber: {
    fontFamily: 'monospace',
  },
  duration: {
    fontFamily: 'monospace',
  },
  expandButton: {
    minWidth: '28px',
    width: '28px',
    height: '28px',
    padding: '0',
  },
  expandCell: {
    width: '36px',
    minWidth: '36px',
    padding: '0',
  },
  expandedSubRow: {
    '& > td': {
      padding: '0 !important',
    },
  },
});

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

interface CallListProps {
  emulationPanelOpen: boolean;
  onToggleEmulationPanel: () => void;
}

export function CallList({ emulationPanelOpen, onToggleEmulationPanel }: CallListProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { formatDateTime, timezone } = useTimezone();

  const [data, setData] = useState<CallListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const [customers, setCustomers] = useState<string[]>([])
  const [allBranches, setAllBranches] = useState<{ name: string; customer_name?: string }[]>([]);

  // Expand/collapse state for call legs
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [legsData, setLegsData] = useState<Record<string, CallLegsResponse>>({});
  const [legsLoading, setLegsLoading] = useState<Set<string>>(new Set());
  const [legsErrors, setLegsErrors] = useState<Record<string, string>>({});

  // Get current filter values from URL
  const currentCustomer = searchParams.get('customer_name') || '';
  const currentBranch = searchParams.get('branch_name') || '';
  const currentSearch = searchParams.get('search') || '';
  const currentDirection = searchParams.get('direction') || '';
  const currentQuality = searchParams.get('quality') || '';
  const currentStartDate = searchParams.get('start_date') || '';
  const currentEndDate = searchParams.get('end_date') || '';
  const needsDefaultDateRange = !['date_preset', 'start_date', 'end_date']
    .some((key) => searchParams.has(key));
  const currentDatePreset = needsDefaultDateRange
    ? 'today'
    : getStoredDatePreset(searchParams, currentStartDate, currentEndDate, timezone);
  const filtersKey = searchParams.toString();
  const [showCustomDates, setShowCustomDates] = useState(currentDatePreset === 'custom');

  // Local search input state (synced with URL)
  const [searchInput, setSearchInput] = useState(currentSearch);

  // "Report missing call" dialog (spec §7.4, Task 14)
  const [reportMissingCallOpen, setReportMissingCallOpen] = useState(false);

  // Filter branches by selected customer
  const filteredBranches = currentCustomer
    ? allBranches.filter(b => !b.customer_name || b.customer_name === currentCustomer)
    : allBranches;

  // Sync search input with URL param when it changes externally
  useEffect(() => {
    setSearchInput(currentSearch);
  }, [currentSearch]);

  useEffect(() => {
    if (currentDatePreset === 'custom') {
      setShowCustomDates(true);
    }
  }, [currentDatePreset]);

  // Clear branch selection when customer changes (if branch doesn't belong to new customer)
  // Only run this check after branches have been loaded to avoid clearing branch on initial load
  useEffect(() => {
    // Don't clear branch if branches haven't been loaded yet
    if (allBranches.length === 0) return;

    if (currentBranch && currentCustomer) {
      const branchBelongsToCustomer = filteredBranches.some(b => b.name === currentBranch);
      if (!branchBelongsToCustomer) {
        // Clear branch selection
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('branch_name');
        setSearchParams(newParams);
      }
    }
  }, [currentCustomer, currentBranch, filteredBranches, allBranches.length, searchParams, setSearchParams]);

  // Fetch customers and branches list
  useEffect(() => {
    async function fetchFilterOptions() {
      if (!isAuthenticated) return;
      try {
        const [customerList, branchList] = await Promise.all([
          api.getCustomers(),
          api.getBranches(),
        ]);
        setCustomers(customerList);
        setAllBranches(branchList);
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    }
    fetchFilterOptions();
  }, [isAuthenticated]);

  const updateFilters = useCallback(
    (updates: Partial<CallFilters> & { date_preset?: string }) => {
      const newParams = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (key === 'date_preset' && value) {
          newParams.set(key, String(value));
        } else if (value && value !== 'all') {
          newParams.set(key, String(value));
        } else {
          newParams.delete(key);
        }
      });

      // Reset to page 1 when filters change (except page itself)
      if (!('page' in updates)) {
        newParams.set('page', '1');
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (!isAuthenticated || !needsDefaultDateRange) return;

    const defaultParams = buildDefaultCallHistorySearchParams(searchParams, timezone);
    if (defaultParams) {
      setSearchParams(defaultParams, { replace: true });
    }
  }, [isAuthenticated, needsDefaultDateRange, searchParams, setSearchParams, timezone]);

  useEffect(() => {
    if (!isAuthenticated || needsDefaultDateRange) return;
    const requestId = ++requestIdRef.current;
    const controller = new AbortController();

    async function fetchCalls() {
      // Parse filters from current searchParams
      // External users always use their assigned customer filter
      const effectiveCustomer = user?.is_internal === false
        ? user.customer_filter || undefined
        : searchParams.get('customer_name') || undefined;

      const currentFilters: CallFilters = {
        direction: searchParams.get('direction') || undefined,
        quality: searchParams.get('quality') || undefined,
        customer_name: effectiveCustomer,
        branch_name: searchParams.get('branch_name') || undefined,
        search: searchParams.get('search') || undefined,
        start_date: searchParams.get('start_date') || undefined,
        end_date: searchParams.get('end_date') || undefined,
        page: parseInt(searchParams.get('page') || '1', 10),
        page_size: 25,
      };

      try {
        setIsLoading(true);
        setError(null);
        const result = await api.getCalls(currentFilters, timezone, controller.signal);
        if (requestId !== requestIdRef.current) return;
        setData(result);
        setExpandedRows(new Set());
        setError(null);
      } catch (err) {
        if (controller.signal.aborted || requestId !== requestIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load calls');
        console.error('Failed to fetch calls:', err);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }

    fetchCalls();
    return () => {
      controller.abort();
      if (requestIdRef.current === requestId) {
        requestIdRef.current += 1;
      }
    };
  }, [isAuthenticated, needsDefaultDateRange, filtersKey, searchParams, timezone, user?.customer_filter, user?.is_internal]);

  const handleSearch = () => {
    updateFilters({ search: searchInput || undefined });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const toggleExpand = useCallback(async (multiSessionId: string) => {
    const isExpanded = expandedRows.has(multiSessionId);
    const next = new Set(expandedRows);
    if (isExpanded) {
      next.delete(multiSessionId);
      setExpandedRows(next);
      return;
    }
    next.add(multiSessionId);
    setExpandedRows(next);

    // Fetch legs data if not already loaded
    if (!legsData[multiSessionId]) {
      setLegsLoading(prev => new Set(prev).add(multiSessionId));
      try {
        const result = await api.getCallLegs(multiSessionId);
        setLegsData(prev => ({ ...prev, [multiSessionId]: result }));
      } catch (err) {
        setLegsErrors(prev => ({
          ...prev,
          [multiSessionId]: err instanceof Error ? err.message : 'Failed to load legs',
        }));
      } finally {
        setLegsLoading(prev => {
          const next = new Set(prev);
          next.delete(multiSessionId);
          return next;
        });
      }
    }
  }, [expandedRows, legsData]);

  const handleCustomerChange = (value: string | undefined) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('customer_name', value);
    } else {
      newParams.delete('customer_name');
    }
    // Clear branch when customer changes
    newParams.delete('branch_name');
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleDatePresetChange = (preset: string | undefined) => {
    const normalizedPreset = (preset || 'all') as DatePreset;

    if (normalizedPreset === 'custom') {
      setShowCustomDates(true);
      if (!currentStartDate && !currentEndDate) {
        const range = buildPresetRange('today', timezone);
        updateFilters({
          date_preset: 'custom',
          start_date: range.start,
          end_date: range.end,
        });
      } else {
        updateFilters({ date_preset: 'custom' });
      }
      return;
    }

    setShowCustomDates(false);
    const range = buildPresetRange(normalizedPreset, timezone);
    updateFilters({
      date_preset: normalizedPreset,
      start_date: range.start,
      end_date: range.end,
    });
  };

  const hasLoadedData = Boolean(data);
  const showInitialLoading = isLoading && !hasLoadedData;
  const showRefreshing = isLoading && hasLoadedData;

  const calls = data?.calls ?? [];
  const showCustomer = user?.is_internal !== false;
  const ownedSet = new Set((user?.owned_numbers ?? []).map(normalizePhoneForCompare));
  const hideTo = calls.length > 0 && calls.every((c) => ownedSet.has(normalizePhoneForCompare(c.called_number)));
  const visibleColumnCount = 6 // expand, time, direction, from, duration, outcome
    + (hideTo ? 0 : 1)
    + (showCustomer ? 1 : 0)
    + 1 // branch
    + 1; // quality

  return (
    <div>
      <PageHeader
        title="Call History"
        subtitle={data ? `${data.total_count.toLocaleString()} calls` : undefined}
        breadcrumb="Calls"
        actions={
          <ViewAsAction
            emulationPanelOpen={emulationPanelOpen}
            onToggleEmulationPanel={onToggleEmulationPanel}
          />
        }
      />

      <div className={styles.content}>
      <div className={styles.backRow}>
        <Button
          appearance="subtle"
          icon={<ArrowLeftFilled />}
          onClick={() => {
            const params = new URLSearchParams();
            for (const key of ['customer_name', 'branch_name', 'start_date', 'end_date', 'date_preset']) {
              const value = searchParams.get(key);
              if (value) {
                params.set(key, value);
              }
            }
            const destination =
              user?.role === 'platform_admin' || user?.role === 'tenant_admin'
                ? '/dashboard'
                : '/my';
            navigate(`${destination}${params.toString() ? `?${params.toString()}` : ''}`);
          }}
        >
          Back
        </Button>
      </div>

      <div className={styles.filters}>
        <Input
          className={styles.searchInput}
          placeholder="Search phone numbers, names..."
          value={searchInput}
          onChange={(_, data) => setSearchInput(data.value)}
          onKeyDown={handleKeyDown}
          contentAfter={
            <Button
              appearance="transparent"
              icon={<SearchRegular />}
              onClick={handleSearch}
            />
          }
        />

        <Button appearance="secondary" onClick={() => setReportMissingCallOpen(true)}>
          {strings.callHistory.reportMissing}
        </Button>

        {user?.is_internal !== false && (
          <Dropdown
            className={styles.filterItem}
            placeholder="Customer"
            value={currentCustomer || 'All Customers'}
            onOptionSelect={(_, data) =>
              handleCustomerChange(data.optionValue || undefined)
            }
          >
            <Option value="">All Customers</Option>
            {customers.map((customer) => (
              <Option key={customer} value={customer}>
                {customer}
              </Option>
            ))}
          </Dropdown>
        )}

        {filteredBranches.length > 0 && (
          <Dropdown
            className={styles.filterItem}
            placeholder={user?.location_label_singular ?? 'Branch'}
            value={currentBranch || `All ${user?.location_label_plural ?? 'Branches'}`}
            onOptionSelect={(_, data) =>
              updateFilters({ branch_name: data.optionValue })
            }
          >
            <Option value="" text={`All ${user?.location_label_plural ?? 'Branches'}`}>
              All {user?.location_label_plural ?? 'Branches'}
            </Option>
            {filteredBranches.map((branch) => (
              <Option key={branch.name} value={branch.name}>
                {branch.name}
              </Option>
            ))}
          </Dropdown>
        )}

        <Dropdown
          className={styles.filterItem}
          placeholder="Direction"
          value={currentDirection || 'All Directions'}
          onOptionSelect={(_, data) =>
            updateFilters({ direction: data.optionValue })
          }
        >
          <Option value="">All Directions</Option>
          <Option value="inbound">Inbound</Option>
          <Option value="outbound">Outbound</Option>
        </Dropdown>

        <Dropdown
          className={styles.filterItem}
          placeholder="Quality"
          value={currentQuality || 'All Quality'}
          onOptionSelect={(_, data) =>
            updateFilters({ quality: data.optionValue })
          }
        >
          <Option value="">All Quality</Option>
          <Option value="good">Good</Option>
          <Option value="warning">Warning</Option>
          <Option value="poor">Poor</Option>
        </Dropdown>

        <Dropdown
          className={styles.filterItem}
          placeholder="Date Range"
          value={
            currentDatePreset === 'all' ? 'All Time'
              : currentDatePreset === 'today' ? 'Today'
              : currentDatePreset === 'yesterday' ? 'Yesterday'
              : currentDatePreset === 'last7' ? 'Last 7 Days'
              : currentDatePreset === 'last30' ? 'Last 30 Days'
              : 'Custom'
          }
          onOptionSelect={(_, data) => handleDatePresetChange(data.optionValue)}
        >
          <Option value="all">All Time</Option>
          <Option value="today">Today</Option>
          <Option value="yesterday">Yesterday</Option>
          <Option value="last7">Last 7 Days</Option>
          <Option value="last30">Last 30 Days</Option>
          <Option value="custom">Custom</Option>
        </Dropdown>

        {showCustomDates && (
          <NativeDateInput
            value={currentStartDate}
            max={currentEndDate || undefined}
            onChange={(value) => updateFilters({
              date_preset: 'custom',
              start_date: value || undefined,
            })}
          />
        )}

        {showCustomDates && (
          <NativeDateInput
            value={currentEndDate}
            min={currentStartDate || undefined}
            onChange={(value) => updateFilters({
              date_preset: 'custom',
              end_date: value || undefined,
            })}
          />
        )}

        <TimezoneSelector />
      </div>

      {showInitialLoading ? (
        <LoadingState variant="page" label="Loading calls…" />
      ) : (
        <>
          {showRefreshing && (
            <Card appearance="filled" className={styles.statusBanner}>
              <CardHeader
                header={
                  <div className={styles.statusBannerContent}>
                    <Spinner size="tiny" label="Refreshing calls..." />
                    <Text weight="semibold">Refreshing current results</Text>
                    <Text className={styles.statusBannerText}>
                      Keeping the current call list visible while we fetch the latest filters.
                    </Text>
                  </div>
                }
              />
            </Card>
          )}

          {error && (
            <ErrorState
              className={styles.statusBannerError}
              title="Could not refresh calls"
              message={error}
            />
          )}

          <div className={styles.tableContainer}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell className={styles.expandCell} />
                  <TableHeaderCell className={styles.timeCell}>Time</TableHeaderCell>
                  <TableHeaderCell>Direction</TableHeaderCell>
                  <TableHeaderCell>From</TableHeaderCell>
                  {!hideTo && <TableHeaderCell>To</TableHeaderCell>}
                  <TableHeaderCell>Duration</TableHeaderCell>
                  <TableHeaderCell>{strings.callHistory.outcome}</TableHeaderCell>
                  {showCustomer && <TableHeaderCell>Customer</TableHeaderCell>}
                  <TableHeaderCell>{user?.location_label_singular ?? 'Branch'}</TableHeaderCell>
                  <TableHeaderCell>Quality</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calls.map((call) => {
                  const isExpanded = call.multi_session_id ? expandedRows.has(call.multi_session_id) : false;
                  const canExpand = call.has_expandable_legs && !!call.multi_session_id;

                  const outcomeWord = deriveOutcome({ direction: call.direction, outcome: call.outcome ?? null });
                  const outcomeTonality = outcomeTone(outcomeWord);

                  return (
                    <Fragment key={call.session_id}>
                      <TableRow
                        className={styles.clickableRow}
                        onClick={() => navigate(interactionPathFor(call.session_id))}
                      >
                        <TableCell className={styles.expandCell}>
                          {canExpand && (
                            <Button
                              className={styles.expandButton}
                              appearance="subtle"
                              size="small"
                              icon={isExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(call.multi_session_id!);
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell className={styles.timeCell}>
                          <ReportingTimeDisplay value={call.reporting_time} basis={call.reporting_time_basis} format={formatDateTime} />
                          {call.incomplete_leg_evidence && <Text size={200}>Partial journey timing</Text>}
                        </TableCell>
                        <TableCell>
                          <StatusPill className={styles.directionBadge} tone="info">
                            {call.direction}
                          </StatusPill>
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className={styles.phoneNumber}>
                              {formatPhoneNumber(call.calling_number)}
                            </span>
                            {call.calling_name && call.calling_name !== call.calling_number && (
                              <div>
                                <Text size={200}>{call.calling_name}</Text>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        {!hideTo && (
                          <TableCell>
                            <span className={styles.phoneNumber}>
                              {formatPhoneNumber(call.called_number)}
                            </span>
                          </TableCell>
                        )}
                        <TableCell className={styles.duration}>
                          {formatDuration(call.duration_seconds)}
                          {(() => {
                            const isVoiceApp = call.assignment_type === 'Voice app assigned';
                            const isAutoAttendant = call.assignment_name?.includes('Auto Attendant') ||
                                                    call.assignment_name?.includes('AA-');
                            const hasMultipleLegs = call.leg_count > 2;

                            if (isVoiceApp || hasMultipleLegs) {
                              const label = isVoiceApp
                                ? `Routed via ${isAutoAttendant ? 'Auto Attendant' : 'Call Queue'}`
                                : `${call.leg_count} legs (transferred)`;
                              return (
                                <Tooltip content={label} relationship="label">
                                  <StatusPill tone="info" className={styles.legsIconBadge}>
                                    <ArrowSwapFilled />
                                  </StatusPill>
                                </Tooltip>
                              );
                            }
                            return null;
                          })()}
                        </TableCell>
                        <TableCell>
                          <StatusPill
                            tone={outcomeTonality === 'negative' ? 'negative' : outcomeTonality === 'warning' ? 'warning' : 'neutral'}
                          >
                            {outcomeWord}
                          </StatusPill>
                        </TableCell>
                        {showCustomer && <TableCell>{call.customer_name || '-'}</TableCell>}
                        <TableCell>{call.branch_name || '-'}</TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <QualityCell
                              rating={call.quality_rating.overall}
                              technical={user?.can_see_technical_details === true}
                              tooltip={`Jitter: ${call.avg_jitter_ms?.toFixed(1) ?? '-'}ms, Latency: ${call.avg_latency_ms?.toFixed(1) ?? '-'}ms`}
                            />
                            {user?.can_see_technical_details === true && call.has_teams_data && (
                              <Tooltip content="Teams data available" relationship="label">
                                <StatusPill tone="neutral" outline>T</StatusPill>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && call.multi_session_id && (
                        <TableRow key={`${call.session_id}-legs`} className={styles.expandedSubRow}>
                          <TableCell colSpan={visibleColumnCount} style={{ padding: 0 }}>
                            <CallLegsDetail
                              data={legsData[call.multi_session_id] || null}
                              isLoading={legsLoading.has(call.multi_session_id)}
                              error={legsErrors[call.multi_session_id] || null}
                              multiSessionId={call.multi_session_id}
                              canSeeTechnical={user?.can_see_technical_details}
                              canExportDebug={user?.actual_role === 'platform_admin'}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
            {data && data.calls.length === 0 && (
              <EmptyState compact title="No calls match these filters" hint="Try widening the date range or clearing a filter." />
            )}
            {calls.length > 0 && (
              <Text size={200} className={styles.statusBannerText}>{strings.callHistory.durationFootnote}</Text>
            )}
          </div>

          {data && data.total_pages > 1 && (
            <div className={styles.pagination}>
              <Text>
                Page {data.page} of {data.total_pages}
              </Text>
              <div className={styles.paginationButtons}>
                <Button
                  appearance="subtle"
                  icon={<ChevronLeftFilled />}
                  disabled={data.page <= 1}
                  onClick={() => updateFilters({ page: data.page - 1 })}
                >
                  Previous
                </Button>
                <Button
                  appearance="subtle"
                  icon={<ChevronRightFilled />}
                  iconPosition="after"
                  disabled={data.page >= data.total_pages}
                  onClick={() => updateFilters({ page: data.page + 1 })}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      </div>
      <ReportMissingCallDialog
        open={reportMissingCallOpen}
        onClose={() => setReportMissingCallOpen(false)}
        onSubmitted={() => undefined}
      />
    </div>
  );
}
